import { MOBILE_SOURCES } from '@/pages/intelligence/_config/storeRank/config';
import {
  analysisPlatformTopInfo,
  getStoreRankPlatformName,
} from '@/pages/intelligence/_config/storeRank/utils';
import { toUpperFirstCase } from '@/pages/intelligence/utils/topchartsUtils';
import { getPlatformStoreTops } from '@intelligence/v3/_config/platformStoreTops';
import { getCountryByCode } from '@intelligence/v3/utils/region';
import { MAX_ROWS_LIMIT } from '@intelligence/components/DownloadCommon/DownloadPage/utils';
import { shouldUseSSE } from '@intelligence/components/DownloadCommon/DownloadPage/utils/sse';

export const STORE_RANK_METRICS_API =
  '/api/v1/intelligence_pc/game/store_rank/metrics';

const PC_STEAM_SOURCES = [
  'steam top sellers',
  'steam top wish lists',
  'steam most played',
  'steam new and trending',
];

const PC_EPIC_SOURCES = ['epic top sellers', 'epic top popular'];

const DOWNLOAD_PAGE_SIZE = 500;

const STORE_RANK_DIMENSION_TIME = 'time';
const STORE_RANK_DIMENSION_GAME = 'game';

const isTruthyFlag = (value: unknown) =>
  value === 1 || value === '1' || value === true;

const getSourceOrderMap = (params: Record<string, any> = {}) => {
  const selectedSources = getSources(params);
  const rankingSources = Array.isArray(params?.rankingSources)
    ? params.rankingSources
    : selectedSources;
  const selectedSet = new Set(
    selectedSources.map(source => String(source).toLowerCase()),
  );

  const orderedSources = rankingSources.filter(source =>
    selectedSet.has(String(source).toLowerCase()),
  );
  selectedSources.forEach(source => {
    const sourceKey = String(source).toLowerCase();
    if (!orderedSources.some(item => String(item).toLowerCase() === sourceKey)) {
      orderedSources.push(source);
    }
  });

  const sourceOrderMap = new Map<string, number>();
  orderedSources.forEach((source, index) => {
    sourceOrderMap.set(String(source).toLowerCase(), index);
  });
  return sourceOrderMap;
};

const getSourceSortIndex = (
  source: unknown,
  sourceOrderMap: Map<string, number>,
) => {
  const sourceKey = String(source || '').toLowerCase();
  return sourceOrderMap.get(sourceKey) ?? Number.POSITIVE_INFINITY;
};

const getRowDimension = (
  item: Record<string, any>,
  params?: Record<string, any>,
) => {
  const dimension =
    item?.top_type || item?.dimension || item?.select_top_type || '';
  if (dimension === STORE_RANK_DIMENSION_GAME) {
    return STORE_RANK_DIMENSION_GAME;
  }
  if (dimension === STORE_RANK_DIMENSION_TIME) {
    return STORE_RANK_DIMENSION_TIME;
  }
  return params?.select_top_type === STORE_RANK_DIMENSION_GAME
    ? STORE_RANK_DIMENSION_GAME
    : STORE_RANK_DIMENSION_TIME;
};

const getDimensionSortIndex = (dimension: string) =>
  dimension === STORE_RANK_DIMENSION_GAME ? 1 : 0;

const buildStoreRankMetricsColumns = (params: Record<string, any> = {}) => {
  const genreShow = isTruthyFlag(
    params?.genre_show ?? params?.data_sources?.genre_show,
  );
  const releaseDateShow = isTruthyFlag(
    params?.release_date_show ?? params?.data_sources?.release_date_show,
  );

  const columns = [
    { title: 'Store Rank', key: 'store_rank' },
    { title: 'Date', key: 'date' },
    { title: 'Rank', key: 'rank' },
    { title: 'Game', key: 'name' },
    { title: 'Country_Chinese', key: 'country_chinese' },
    { title: 'Country_English', key: 'country_english' },
  ];

  if (releaseDateShow) {
    columns.push({ title: 'Release Date', key: 'release_time' });
  }
  if (genreShow) {
    columns.push(
      { title: 'Main Genre', key: 'mainGenre' },
      { title: 'Sub Genre', key: 'genre_source' },
    );
  }

  columns.push({ title: 'Change', key: 'rank_change' });

  return columns;
};

const getSources = (params: Record<string, any>): string[] => {
  if (Array.isArray(params?.sources) && params.sources.length > 0) {
    return params.sources;
  }
  if (Array.isArray(params?.rankingSources) && params.rankingSources.length > 0) {
    return params.rankingSources;
  }
  if (params?.topchart_name) {
    return [params.topchart_name];
  }
  if (params?.source) {
    return [params.source];
  }

  const entityType = params?.entity_type;
  const store = params?.store;

  if (entityType === 'mobile') {
    if (!store) {
      return [...MOBILE_SOURCES];
    }
    return MOBILE_SOURCES.filter(source => source.startsWith(`${store} `));
  }

  if (entityType === 'pc') {
    if (store === 'steam') {
      return PC_STEAM_SOURCES;
    }
    if (store === 'epic') {
      return PC_EPIC_SOURCES;
    }
  }

  if (entityType === 'console' && store) {
    return getPlatformStoreTops(store);
  }

  return [];
};

const buildGenreFilter = (params: Record<string, any>) => {
  const filter: Record<string, any> = {};
  const gameIds = params?.search_ids || params?.filter?.game_ids;

  if (Array.isArray(gameIds) && gameIds.length > 0) {
    filter.game_ids = gameIds;
  }

  if (params?.genre_source === 'iegg' && params?.iegg_genre_req?.length > 0) {
    filter.iegg_genre = params.iegg_genre_req;
  }

  if (params?.genre_source === 'sensortower') {
    if (params?.genre?.length > 0 || params?.sub_genre?.length > 0) {
      filter.sensortower_genre = [
        {
          genre: params?.genre || [],
          sub_genre: params?.sub_genre || [],
        },
      ];
    }
  }

  return Object.keys(filter).length > 0 ? filter : undefined;
};

export const buildStoreRankMetricsApiParams = (
  params: Record<string, any> = {},
) => {
  const { dateRange = [] } = params;
  const [startDate, endDate] = dateRange;
  const dataMethod = params?.data_method;
  const selectTopType = params?.select_top_type;
  const selectTop = Number(params?.select_top) || 0;
  const markets = params?.regions || params?.markets || ['global'];
  const entityType = params?.entity_type;
  const sources = getSources(params);

  const apiParams: Record<string, any> = {
    start_date: startDate || params?.start_date || '',
    end_date: endDate || params?.end_date || '',
    markets,
    sources,
    entity_types: entityType ? [entityType] : ['mobile'],
    granularity: params?.granularity || 'daily',
    // 榜单顺序由前端按用户勾选顺序重排；后端负责日期、排名等通用排序
    order_by: ['date desc', 'rank asc'],
    page: params?.page || 1,
    page_size: params?.page_size || DOWNLOAD_PAGE_SIZE,
    use_market_info: ['market -> en', 'market -> cn'],
    use_sse_page: {
      max_size: MAX_ROWS_LIMIT,
    },
    use_entity_type: 'app',
  };

  const filter = buildGenreFilter(params);
  if (filter) {
    apiParams.filter = filter;
  }

  const isSelectTopDownload =
    dataMethod === 'selectTop' || (selectTop > 0 && dataMethod !== 'selectAll');

  if (isSelectTopDownload && selectTop > 0) {
    apiParams.top = {
      type: selectTopType || STORE_RANK_DIMENSION_TIME,
      order_by: ['rank asc'],
      size: selectTop,
    };
  }

  const useCustomFields: string[] = [];
  if (
    isTruthyFlag(
      params?.release_date_show ?? params?.data_sources?.release_date_show,
    )
  ) {
    useCustomFields.push('release_time');
  }
  if (
    isTruthyFlag(params?.genre_show ?? params?.data_sources?.genre_show)
  ) {
    if (entityType === 'pc' || entityType === 'console') {
      useCustomFields.push('iegg_genre', 'iegg_sub_genre');
    } else if (params?.genre_source === 'iegg') {
      useCustomFields.push('iegg_genre', 'iegg_sub_genre');
    } else if (params?.genre_source === 'annie') {
      useCustomFields.push('genre', 'sub_genre');
    }
  }
  if (useCustomFields.length > 0) {
    apiParams.use_custom_fields = useCustomFields;
  }

  return apiParams;
};

export const isStoreRankMetricsRow = (item: Record<string, any>) => {
  if (!item) {
    return false;
  }

  return (
    Reflect.has(item, 'date') &&
    Reflect.has(item, 'rank') &&
    (Reflect.has(item, 'game_name') ||
      Reflect.has(item, 'name') ||
      Reflect.has(item, 'game_id'))
  );
};

export const normalizeStoreRankMetricsRows = (
  data: Record<string, any>[],
  params?: Record<string, any>,
) => {
  if (!Array.isArray(data)) {
    return [];
  }

  const regionOptions = params?.regionOptions || [];
  const marketNameENMap = params?.market_info?.['market -> en'] || {};
  const marketNameCNMap = params?.market_info?.['market -> cn'] || {};

  return data.map(item => {
    const marketCode = String(item?.market || '').toLowerCase();
    const country = getCountryByCode(marketCode, regionOptions);
    const rankRatio = item?.rank_ratio;
    const entityInfo =
      params?.entity_info?.[item?.game_id] ||
      params?.entity_info?.[item?.unified_id] ||
      {};
    const extField = (key: string) =>
      entityInfo?.[key] || item?.[key] || '';

    const rawSource = item?.source || item?.store || '';
    const { platform, top } = analysisPlatformTopInfo(rawSource);
    const entityType = params?.entity_type;
    const platformName =
      entityType && entityType !== 'minigame' && platform
        ? getStoreRankPlatformName(entityType, platform)
        : '';
    const topLabel =
      top === 'top wish lists'
        ? 'Top Wishlists'
        : top
            .split(' ')
            .map(word => toUpperFirstCase(word))
            .join(' ');
    const storeRankLabel =
      platformName && topLabel
        ? `${platformName} ${topLabel}`
        : rawSource.split(' ').map(word => toUpperFirstCase(word)).join(' ');

    const row: Record<string, any> = {
      ...item,
      store_rank: storeRankLabel,
      name: item?.game_name || item?.name || '',
      release_time: extField('release_time') || item?.release_date || '',
      country_chinese:
        country?.country_name_ch ||
        marketNameCNMap?.[marketCode] ||
        marketCode,
      country_english:
        country?.country_name_en ||
        marketNameENMap?.[marketCode] ||
        marketCode,
      rank_change:
        rankRatio === null || rankRatio === undefined || rankRatio === ''
          ? '-'
          : String(Number(rankRatio)),
    };

    if (
      isTruthyFlag(params?.genre_show ?? params?.data_sources?.genre_show)
    ) {
      if (entityType === 'pc' || entityType === 'console') {
        row.mainGenre = extField('iegg_genre');
        row.genre_source = extField('iegg_sub_genre');
      } else if (params?.genre_source === 'iegg') {
        row.mainGenre = extField('iegg_genre');
        row.genre_source = extField('iegg_sub_genre');
      } else if (params?.genre_source === 'annie') {
        row.mainGenre = extField('genre');
        row.genre_source = extField('sub_genre');
      } else if (params?.genre_source === 'appmagic') {
        row.mainGenre = item?.super_genre || '';
        row.genre_source = [item?.major_category, item?.mid_category]
          .filter(Boolean)
          .join(' | ');
      } else {
        row.mainGenre = item?.sensortower_genre || '';
        row.genre_source = item?.sensortower_sub_genre || '';
      }
    }

    return row;
  });
};

export const sortStoreRankMetricsRows = (
  data: Record<string, any>[],
  params?: Record<string, any>,
) => {
  if (!Array.isArray(data)) {
    return [];
  }

  const sourceOrderMap = getSourceOrderMap(params || {});

  return [...data].sort((prev, next) => {
    const sourceDiff =
      getSourceSortIndex(prev?.source || prev?.store, sourceOrderMap) -
      getSourceSortIndex(next?.source || next?.store, sourceOrderMap);
    if (sourceDiff !== 0) {
      return sourceDiff;
    }

    const prevDate = prev?.date ? new Date(prev.date).getTime() : 0;
    const nextDate = next?.date ? new Date(next.date).getTime() : 0;
    if (prevDate !== nextDate) {
      return nextDate - prevDate;
    }

    const dimensionDiff =
      getDimensionSortIndex(getRowDimension(prev, params)) -
      getDimensionSortIndex(getRowDimension(next, params));
    if (dimensionDiff !== 0) {
      return dimensionDiff;
    }

    const prevRank = Number(prev?.rank);
    const nextRank = Number(next?.rank);
    const safePrevRank = Number.isFinite(prevRank)
      ? prevRank
      : Number.POSITIVE_INFINITY;
    const safeNextRank = Number.isFinite(nextRank)
      ? nextRank
      : Number.POSITIVE_INFINITY;
    return safePrevRank - safeNextRank;
  });
};

export const createStoreRankMetricsDownloadConfig = (
  getFilename: (params: Record<string, any>) => string,
) => {
  return (params: Record<string, any>) => {
    const downloadApiParams = buildStoreRankMetricsApiParams(params);
    const useSSE = shouldUseSSE(downloadApiParams);
    const [startDate = '', endDate = ''] = params?.dateRange || [];

    return {
      isUsingNewMethod: false,
      dataKey: ['list'],
      fetchOnlyOnce: useSSE,
      $$fileName:
        params?.new_style_donwload_file_name?.replace(
          /_\d{4}-\d{2}-\d{2}_\d{4}-\d{2}-\d{2}$/,
          `_${startDate}_${endDate}`,
        ) || getFilename(params),
      downloadFetchApi: STORE_RANK_METRICS_API,
      downloadApiParams,
      sheetName: ['Store Rank Top'],
      'Store Rank Top': buildStoreRankMetricsColumns(params),
      regionOptions: params?.regionOptions,
    };
  };
};
